import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  useTheme,
  alpha,
  Avatar,
  Stack,
  Divider,
  Button,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  LocationCity as LocationCityIcon,
  TrendingUp as TrendingUpIcon,
  ShoppingCart as ShoppingCartIcon,
  MonetizationOn as MonetizationOnIcon,
  Refresh as RefreshIcon,
  GetApp as ExportIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ru } from 'date-fns/locale';
import { startOfMonth, endOfMonth, subMonths } from 'date-fns';
import axiosInstance from '../utils/axios';
import { useSnackbar } from 'notistack';

const CityStats = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  
  const [loading, setLoading] = useState(false);
  const [cities, setCities] = useState([]);
  const [statsData, setStatsData] = useState([]);
  const [startDate, setStartDate] = useState(startOfMonth(subMonths(new Date(), 2)));
  const [endDate, setEndDate] = useState(endOfMonth(new Date()));

  useEffect(() => {
    fetchCities();
  }, []);

  useEffect(() => {
    if (cities.length > 0) {
      fetchAllCitiesStats();
    }
  }, [cities, startDate, endDate]);

  const fetchCities = async () => {
    try {
      const response = await axiosInstance.get('/addresses/cities');
      setCities(response.data.data || []);
    } catch (error) {
      console.error('Error fetching cities:', error);
      enqueueSnackbar('Ошибка загрузки городов', { variant: 'error' });
    }
  };

  const fetchAllCitiesStats = async () => {
    setLoading(true);
    try {
      const promises = cities.map(city => 
        fetchCityStats(city._id, city.name)
      );
      
      const results = await Promise.all(promises);
      setStatsData(results.filter(r => r !== null));
    } catch (error) {
      console.error('Error fetching stats:', error);
      enqueueSnackbar('Ошибка загрузки статистики', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchCityStats = async (cityId, cityName) => {
    try {
      // Получаем статистику по выданным адресам
      const issuedResponse = await axiosInstance.get('/stats/issued', {
        params: {
          city: cityId,
          startDate: startDate?.toISOString(),
          endDate: endDate?.toISOString()
        }
      });

      // Получаем статистику по активным позициям
      const productsResponse = await axiosInstance.get('/stats/products', {
        params: {
          city: cityId
        }
      });

      // Получаем статистику продаж по городу
      const salesResponse = await axiosInstance.get('/stats/sales-by-city', {
        params: {
          startDate: startDate?.toISOString(),
          endDate: endDate?.toISOString()
        }
      });

      const citySales = salesResponse.data.cities?.find(c => c.cityId?.toString() === cityId?.toString()) || {
        totalOrders: 0,
        totalAmount: 0,
        totalBase: 0,
        totalCoupon: 0
      };

      return {
        cityId,
        cityName,
        issued: {
          count: issuedResponse.data.totalCount || 0,
          amount: issuedResponse.data.totalAmount || 0,
          packagings: issuedResponse.data.packagings || []
        },
        active: {
          count: productsResponse.data.data?.reduce((sum, p) => sum + p.totalItems, 0) || 0,
          amount: productsResponse.data.data?.reduce((sum, p) => sum + p.totalAmount, 0) || 0,
          products: productsResponse.data.data || []
        },
        sales: {
          count: citySales.totalOrders || 0,
          amount: citySales.totalAmount || 0,
          baseAmount: citySales.totalBase || 0,
          couponAmount: citySales.totalCoupon || 0
        }
      };
    } catch (error) {
      console.error(`Error fetching stats for city ${cityName}:`, error);
      return null;
    }
  };

  const exportToExcel = async () => {
    try {
      const XLSX = await import('xlsx');
      const wb = XLSX.utils.book_new();
      
      const wsData = [
        ['Статистика по городам'],
        [`Период: ${startDate?.toLocaleDateString('ru-RU')} - ${endDate?.toLocaleDateString('ru-RU')}`],
        [],
        ['Город', 'Выдано позиций', 'Сумма выдачи (₽)', 'Активных позиций', 'Сумма активных (₽)', 'Всего позиций', 'Общая сумма (₽)'],
        ...statsData.map(city => [
          city.cityName,
          city.issued.count,
          city.issued.amount,
          city.active.count,
          city.active.amount,
          city.issued.count + city.active.count,
          city.issued.amount + city.active.amount
        ]),
        [],
        ['ИТОГО',
          statsData.reduce((sum, c) => sum + c.issued.count, 0),
          statsData.reduce((sum, c) => sum + c.issued.amount, 0),
          statsData.reduce((sum, c) => sum + c.active.count, 0),
          statsData.reduce((sum, c) => sum + c.active.amount, 0),
          statsData.reduce((sum, c) => sum + c.issued.count + c.active.count, 0),
          statsData.reduce((sum, c) => sum + c.issued.amount + c.active.amount, 0)
        ]
      ];

      const ws = XLSX.utils.aoa_to_sheet(wsData);
      ws['!cols'] = [
        { wch: 20 }, { wch: 18 }, { wch: 20 }, { wch: 20 }, { wch: 22 }, { wch: 18 }, { wch: 20 }
      ];

      XLSX.utils.book_append_sheet(wb, ws, 'Статистика по городам');
      
      const fileName = `Статистика_по_городам_${new Date().toLocaleDateString('ru-RU')}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
      enqueueSnackbar('Экспорт выполнен успешно', { variant: 'success' });
    } catch (error) {
      console.error('Error exporting:', error);
      enqueueSnackbar('Ошибка при экспорте', { variant: 'error' });
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  const totalIssued = statsData.reduce((sum, city) => sum + city.issued.count, 0);
  const totalIssuedAmount = statsData.reduce((sum, city) => sum + city.issued.amount, 0);
  const totalActive = statsData.reduce((sum, city) => sum + city.active.count, 0);
  const totalActiveAmount = statsData.reduce((sum, city) => sum + city.active.amount, 0);
  const totalSales = statsData.reduce((sum, city) => sum + city.sales.count, 0);
  const totalSalesAmount = statsData.reduce((sum, city) => sum + city.sales.amount, 0);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ru}>
      <Container maxWidth="xl" sx={{ pb: 4 }}>
        {/* Header */}
        <Paper
          elevation={4}
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 3,
            p: 3,
            mb: 3,
            color: 'text.primary'
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <Avatar sx={{ bgcolor: 'action.selected', color: 'primary.main', mr: 2, width: 56, height: 56 }}>
                <LocationCityIcon sx={{ fontSize: 28 }} />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  Статистика по городам
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Сравнение продаж по всем городам за выбранный период
                </Typography>
              </Box>
            </Box>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Экспорт в Excel">
                <IconButton
                  onClick={exportToExcel}
                  disabled={loading || statsData.length === 0}
                  sx={{
                    color: 'text.primary',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    '&:hover': { bgcolor: 'action.selected', color: 'primary.main' },
                    '&:disabled': { opacity: 0.5 }
                  }}
                >
                  <ExportIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Обновить">
                <IconButton
                  onClick={fetchAllCitiesStats}
                  disabled={loading}
                  sx={{
                    color: 'text.primary',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    '&:hover': { bgcolor: 'action.selected', color: 'primary.main' },
                    '&:disabled': { opacity: 0.5 }
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
        </Paper>

        {/* Date Filters */}
        <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <DatePicker
                label="Дата начала"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'outlined'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <DatePicker
                label="Дата окончания"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'outlined'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="contained"
                onClick={fetchAllCitiesStats}
                disabled={loading}
                sx={{ height: 56 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Применить'}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card elevation={3} sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <ShoppingCartIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                  <Typography variant="body2" color="text.secondary">
                    Продаж
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {totalSales}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card elevation={3} sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <MonetizationOnIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                  <Typography variant="body2" color="text.secondary">
                    Сумма продаж
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {formatCurrency(totalSalesAmount)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card elevation={3} sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <ShoppingCartIcon sx={{ mr: 1, color: theme.palette.error.main }} />
                  <Typography variant="body2" color="text.secondary">
                    Выдано позиций
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {totalIssued}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card elevation={3} sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <MonetizationOnIcon sx={{ mr: 1, color: theme.palette.error.main }} />
                  <Typography variant="body2" color="text.secondary">
                    Сумма выдачи
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {formatCurrency(totalIssuedAmount)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card elevation={3} sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <TrendingUpIcon sx={{ mr: 1, color: theme.palette.success.main }} />
                  <Typography variant="body2" color="text.secondary">
                    Активных позиций
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {totalActive}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Table */}
        <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" fontWeight="bold">
              Детальная статистика по городам
            </Typography>
          </Box>
          
          {loading ? (
            <Box display="flex" justifyContent="center" p={6}>
              <CircularProgress />
            </Box>
          ) : statsData.length === 0 ? (
            <Box p={6} textAlign="center">
              <LocationCityIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Нет данных
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Город</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Продаж</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Сумма продаж</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Выдано</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Сумма выдачи</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Активных</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Сумма активных</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {statsData.map((city) => (
                    <TableRow key={city.cityId} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <LocationCityIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                          <Typography fontWeight="600">{city.cityName}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={city.sales.count}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold" color="primary">
                          {formatCurrency(city.sales.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={city.issued.count}
                          size="small"
                          color="error"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">{formatCurrency(city.issued.amount)}</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={city.active.count}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">{formatCurrency(city.active.amount)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                    <TableCell>
                      <Typography fontWeight="bold">ИТОГО</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold">{totalSales}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color="primary">{formatCurrency(totalSalesAmount)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold">{totalIssued}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold">{formatCurrency(totalIssuedAmount)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold">{totalActive}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold">{formatCurrency(totalActiveAmount)}</Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Container>
    </LocalizationProvider>
  );
};

export default CityStats;
